import { useState, useEffect, useCallback, useRef } from 'react';

// Import utilities and hooks
import { useRecordModeTTS } from '../../hooks/useMoveVoiceover';
import { getMove } from '../../util/Chess.js';
import BoardThree from '../../components/WorkingChessLogic/3Board.jsx';
import { MobileMovesEngine } from '../components/MobileMoves.jsx';
import chessslogo from '../../images/chessslogo.png';

export const useRecordModeLogic = ({
  // Core game data
  contextMoves,
  contextCurrentMove,
  contextOrientation,
  contextStarredMoves,
  state,
  gameMetadata,
  boardSize,
  
  // Navigation functions
  goToMove,
  
  // View control
  setViewMode,
  
  // Mobile settings
  useChessThreadFont,
  setUseChessThreadFont,
  
  // Board size calculator function
  calculateRecordModeBoardSize,
  
  // Additional required parameters
  stockfishAnalysis,
  stockfishIsAnalyzing,
  gameHasPuzzleTag,
  puzzleIncorrectMove,
  detectNewMove,
  mobileActiveColor,
  mobileArrowMode,
  mobileArrowColor,
  mobileArrowStart,
  boardRef,
  
  // Custom TTS function for intro titles (optional)
  customIntroTTSFunction,
  
  // Intro overlay control
  setShowIntroOverlay,
  
  // Intro text slide-out control
  setIntroTextSlideOut
}) => {
  
  // Record-specific state
  const [recordSubTab, setRecordSubTab] = useState('main'); // 'main' | 'settings' | 'move-enhancements'
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const [totalRecordingTime, setTotalRecordingTime] = useState('0:00');
  const [currentMoveTime, setCurrentMoveTime] = useState('0:00');
  const [localAnnotationEdits, setLocalAnnotationEdits] = useState({}); // Store local edits per move
  
  // Move enhancement state - store per-move visual enhancements
  const [moveEnhancements, setMoveEnhancements] = useState({}); // Store move enhancements per move ID
  
  const autoPlayIntervalRef = useRef(null);
  
  // Title editing state
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const recordTitleInputRef = useRef(null);
  const recordTextareaRef = useRef(null);
  
  // TTS state variables
  const [voiceoverEnabled, setVoiceoverEnabled] = useState(false);
  const [ttsRate, setTtsRate] = useState(() => {
    const saved = localStorage.getItem('chess-voice-rate');
    return saved ? parseFloat(saved) : 1.0;
  });
  const [selectedVoiceIndex, setSelectedVoiceIndex] = useState(() => {
    const saved = localStorage.getItem('chess-record-voice');
    return saved ? parseInt(saved) : null;
  });
  const [availableVoices, setAvailableVoices] = useState([]);
  
  // Playback speed multiplier state
  const [playbackSpeedMultiplier, setPlaybackSpeedMultiplier] = useState(() => {
    const saved = localStorage.getItem('chess-playback-speed');
    return saved ? parseFloat(saved) : 1.0;
  });

  // Custom delay between moves (in milliseconds)
  const [customMoveDelay, setCustomMoveDelay] = useState(() => {
    const saved = localStorage.getItem('chess-move-delay');
    return saved ? parseFloat(saved) : 0;
  });
  
  // Background image state
  const [backgroundImage, setBackgroundImage] = useState(() => {
    const saved = localStorage.getItem('chess-record-background');
    return saved || null;
  });
  
  // Board zoom state - rebuilt for direct size change instead of scale
  const [boardZoomConfig, setBoardZoomConfig] = useState({
    isZoomed: false,    // Toggle for zoom state
    targetSquare: null, // Square to center on when zoomed
    zoomFactor: 2       // How much to multiply board size (2 = double size)
  });
  
  // Debug: Log state changes
  useEffect(() => {
    console.log('🔍 boardZoomConfig state changed:', boardZoomConfig, 'recordSubTab:', recordSubTab);
  }, [boardZoomConfig, recordSubTab]);
  
  // Force render counter to ensure component re-renders
  const [renderCounter, setRenderCounter] = useState(0);
  useEffect(() => {
    if (boardZoomConfig.isZoomed !== (renderCounter % 2 === 1)) {
      console.log('🔍 Forcing render update - renderCounter:', renderCounter + 1);
      setRenderCounter(prev => prev + 1);
    }
  }, [boardZoomConfig.isZoomed, renderCounter]);
  
  // Refs to track current values for autoplay interval closure
  const contextCurrentMoveRef = useRef(contextCurrentMove);
  const contextMovesRef = useRef(contextMoves);
  const localAnnotationEditsRef = useRef(localAnnotationEdits);
  const moveEnhancementsRef = useRef(moveEnhancements);
  const zoomTimeoutRef = useRef(null);

  // Update refs when values change
  useEffect(() => {
    contextCurrentMoveRef.current = contextCurrentMove;
  }, [contextCurrentMove]);

  useEffect(() => {
    contextMovesRef.current = contextMoves;
  }, [contextMoves]);

  useEffect(() => {
    localAnnotationEditsRef.current = localAnnotationEdits;
  }, [localAnnotationEdits]);

  useEffect(() => {
    moveEnhancementsRef.current = moveEnhancements;
  }, [moveEnhancements]);

  // Initialize TTS system with dynamic rate that includes speed multiplier
  const adjustedTtsRate = ttsRate * playbackSpeedMultiplier;
  
  // Check if intro title has Robot TTS enabled for independent TTS
  const introHasRobotTTS = moveEnhancements['-1']?.startPositionIntroTitle?.robotTTS || false;
  
  const {
    speakMoveAnnotation: ttsSpeak,
    stopSpeaking: ttsStop,
    calculateMoveTime,
    isSpeaking
  } = useRecordModeTTS(voiceoverEnabled || introHasRobotTTS, {
    rate: adjustedTtsRate,
    voiceIndex: selectedVoiceIndex
  });

  // Calculate display time for a move based on annotation content length
  const calculateMoveDisplayTime = useCallback((moveIndex, contextMoves, state, localEdits = {}) => {
    const baseTime = 750; // Base time of 0.75 second
    let content = '';
    
    try {
      if (moveIndex === -1) {
        // Start position content - moveId should be '-1' to match contextCurrentMove
        const moveId = '-1';
        const localMoveEdits = localEdits[moveId] || {};
        content = localMoveEdits.content !== undefined ? localMoveEdits.content : (state?.startPosition?.content || '');
        
        // Check if intro is enabled and has content to determine if buffer is needed
        const introTitleData = moveEnhancementsRef.current['-1']?.startPositionIntroTitle;
        const hasIntroContent = introTitleData?.robotTTS && introTitleData?.text;
        const introBuffer = hasIntroContent ? 750 : 0; // Only add buffer when intro is actually used
        
        const textContent = content.replace(/<[^>]*>/g, '').trim();
        const contentLength = textContent.length;
        
        if (contentLength > 20) {
          return (baseTime * 3) + introBuffer; // 3 seconds + buffer (if intro enabled)
        } else if (contentLength > 10) {
          return (baseTime * 2) + introBuffer; // 2.25 seconds + buffer (if intro enabled)
        } else {
          return baseTime + introBuffer; // 1.5 seconds + buffer (if intro enabled)
        }
      } else if (moveIndex >= 0 && moveIndex < contextMoves.length) {
        // Main line move content - check for local edits first
        const move = contextMoves[moveIndex];
        // Try multiple possible moveId formats since contextCurrentMove can vary
        let localMoveEdits = localEdits[`${moveIndex}|main`] || {};
        if (!localMoveEdits.annotation && !localMoveEdits.content) {
          // Also try just the index as string
          localMoveEdits = localEdits[`${moveIndex}`] || {};
        }
        
        const annotation = localMoveEdits.annotation !== undefined ? localMoveEdits.annotation : (move?.annotation || '');
        const moveContent = localMoveEdits.content !== undefined ? localMoveEdits.content : (move?.content || '');
        content = annotation + ' ' + moveContent;
      }
      
      // Count content length (excluding HTML tags for better accuracy)
      const textContent = content.replace(/<[^>]*>/g, '').trim();
      const contentLength = textContent.length;
      
      // Apply timing rules
      if (contentLength > 20) {
        return baseTime * 3; // 2.25 seconds for >20 characters
      } else if (contentLength > 10) {
        return baseTime * 2; // 1.5 seconds for >10 characters  
      } else {
        return baseTime; // 0.75 second for ≤10 characters
      }
    } catch (error) {
      console.error('Error calculating move display time:', error);
      return baseTime;
    }
  }, []);

  // Calculate total recording time for all moves
  const calculateTotalRecordingTime = useCallback((contextMoves, state) => {
    let totalTime = 0;
    
    try {
      // Add time for start position
      totalTime += calculateMoveDisplayTime(-1, contextMoves, state, localAnnotationEdits);
      
      // Add time for each move
      for (let i = 0; i < contextMoves.length; i++) {
        totalTime += calculateMoveDisplayTime(i, contextMoves, state, localAnnotationEdits);
      }
      
      // Convert milliseconds to minutes:seconds.tenths format
      const minutes = Math.floor(totalTime / 60000);
      const totalSeconds = (totalTime % 60000) / 1000;
      const seconds = Math.floor(totalSeconds);
      const tenths = Math.floor((totalSeconds % 1) * 10);
      return `${minutes}:${seconds.toString().padStart(2, '0')}.${tenths}`;
      
    } catch (error) {
      console.error('Error calculating total recording time:', error);
      return '0:00.0';
    }
  }, [calculateMoveDisplayTime, localAnnotationEdits]);

  // Calculate current move time
  const calculateCurrentMoveTime = useCallback((currentMoveId, contextMoves, state, localEdits, enhancements = {}) => {
    try {
      let moveTime = 0;
      
      if (currentMoveId === '-1') {
        // Start position time
        moveTime = calculateMoveDisplayTime(-1, contextMoves, state, localEdits);
      } else if (!currentMoveId.includes('|')) {
        // Main line move
        const moveIndex = parseInt(currentMoveId, 10);
        moveTime = calculateMoveDisplayTime(moveIndex, contextMoves, state, localEdits);
      } else {
        // Variation move - use base timing for now
        moveTime = 1000; // 1 second default for variations
      }
      
      // Add any user time adjustments from move enhancements
      const moveEnhancement = enhancements[currentMoveId];
      const timingData = moveEnhancement?.timing || {};
      const timeAddition = (timingData.timeAddition || 0) * 1000; // Convert seconds to milliseconds
      moveTime += timeAddition;
      
      // Convert milliseconds to simple seconds format (e.g., "1.5s")
      const totalSeconds = moveTime / 1000;
      return `${totalSeconds.toFixed(1)}s`;
      
    } catch (error) {
      console.error('Error calculating current move time:', error);
      return '1.0s';
    }
  }, [calculateMoveDisplayTime]);

  // Move enhancement functions (must be defined before other functions use them)
  const updateMoveEnhancement = useCallback((moveId, enhancementType, enhancementData) => {
    if (!moveId) return;
    
    setMoveEnhancements(prev => ({
      ...prev,
      [moveId]: {
        ...prev[moveId],
        [enhancementType]: enhancementData
      }
    }));
  }, []);

  const getMoveEnhancement = useCallback((moveId, enhancementType) => {
    if (!moveId) return null;
    return moveEnhancements[moveId]?.[enhancementType] || null;
  }, [moveEnhancements]);

  // Get base calculated time (without enhancements) for a move
  const getBaseMoveTime = useCallback((currentMoveId, contextMoves, state, localEdits) => {
    try {
      let moveTime = 0;
      
      if (currentMoveId === '-1') {
        moveTime = calculateMoveDisplayTime(-1, contextMoves, state, localEdits);
      } else if (!currentMoveId.includes('|')) {
        const moveIndex = parseInt(currentMoveId, 10);
        moveTime = calculateMoveDisplayTime(moveIndex, contextMoves, state, localEdits);
      } else {
        moveTime = 1000; // 1 second default for variations
      }
      
      return moveTime / 1000; // Return in seconds
    } catch (error) {
      console.error('Error calculating base move time:', error);
      return 1.0;
    }
  }, [calculateMoveDisplayTime]);

  // Handle time adjustment (plus/minus buttons)
  const adjustMoveTime = useCallback((increment) => {
    if (!contextCurrentMove) return;
    
    const timingEnhancement = getMoveEnhancement(contextCurrentMove, 'timing') || {};
    const currentTimeAddition = timingEnhancement.timeAddition || 0;
    
    // Calculate new time addition
    let newTimeAddition = currentTimeAddition + increment;
    
    // Don't allow going below base time (minimum is 0 additional seconds)
    newTimeAddition = Math.max(0, newTimeAddition);
    
    // Round to nearest 0.1 to avoid floating point issues
    newTimeAddition = Math.round(newTimeAddition * 10) / 10;
    
    // Update move enhancement with time addition
    updateMoveEnhancement(contextCurrentMove, 'timing', {
      ...timingEnhancement,
      timeAddition: newTimeAddition
    });
  }, [contextCurrentMove, getMoveEnhancement, updateMoveEnhancement]);

  // Check if minus button should be disabled
  const canDecreaseMoveTime = useCallback(() => {
    if (!contextCurrentMove) return false;
    
    const timingEnhancement = getMoveEnhancement(contextCurrentMove, 'timing') || {};
    const currentTimeAddition = timingEnhancement.timeAddition || 0;
    
    return currentTimeAddition > 0;
  }, [contextCurrentMove, getMoveEnhancement]);

  // Background image management functions
  const handleBackgroundImageUpload = useCallback((event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target.result;
        setBackgroundImage(imageUrl);
        localStorage.setItem('chess-record-background', imageUrl);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const clearBackgroundImage = useCallback(() => {
    setBackgroundImage(null);
    localStorage.removeItem('chess-record-background');
  }, []);

  const handleColorSwatchSelect = useCallback((color) => {
    // Create a simple colored canvas as background
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 225; // 16:9 aspect ratio
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    const colorDataUrl = canvas.toDataURL();
    setBackgroundImage(colorDataUrl);
    localStorage.setItem('chess-record-background', colorDataUrl);
  }, []);

  // Board zoom helper functions - Enhanced with orientation support
  const getSquarePosition = useCallback((square, boardSize, orientation = 'white') => {
    if (!square || square.length !== 2) return { x: boardSize / 2, y: boardSize / 2 };
    
    // Convert chess square (e.g., 'e4') to pixel coordinates
    const file = square.charCodeAt(0) - 'a'.charCodeAt(0); // 0-7 (a-h)
    const rank = parseInt(square[1]) - 1; // 0-7 (1-8)
    
    const squareSize = boardSize / 8;
    
    // Calculate center of square based on board orientation
    let x, y;
    if (orientation === 'white') {
      x = (file + 0.5) * squareSize;
      y = (7 - rank + 0.5) * squareSize;
    } else {
      // For black orientation, flip both x and y
      x = (7 - file + 0.5) * squareSize;
      y = (rank + 0.5) * squareSize;
    }
    
    return { x, y };
  }, []);

  const analyzeMoveShouldZoom = useCallback((move) => {
    // Auto-zoom disabled - only zoom via move-specific enhancements
    return false;
    
    // LEGACY: Auto-zoom functionality kept for potential reuse
    // if (!move || !boardZoomConfig.autoZoom) return false;
    // return true; // Zoom on every move when auto-zoom was enabled
  }, []);

  const triggerBoardZoom = useCallback((move, duration = 2000) => {
    if (!move || boardZoomConfig.isZoomed) return;
    
    const shouldZoom = analyzeMoveShouldZoom(move);
    if (!shouldZoom) return;
    
    // Check for different possible move square properties
    const endSquare = move.toSquare || move.to || move.endSquare;
    
    if (!endSquare) {
      return;
    }
    
    setBoardZoomConfig({
      isZoomed: true,
      targetSquare: endSquare,
      zoomFactor: 2
    });
    
    // Reset zoom after duration
    setTimeout(() => {
      setBoardZoomConfig({
        isZoomed: false,
        targetSquare: null,
        zoomFactor: 2
      });
    }, duration);
  }, [boardZoomConfig.isZoomed, analyzeMoveShouldZoom]);

  // Move-specific zoom function - Rebuilt for instant size change with debugging
  const triggerMoveSpecificZoom = useCallback((move, duration = 1200) => {
    console.log('🔍 [RecordMode] triggerMoveSpecificZoom called with:', { move, duration });
    
    if (!move) {
      console.log('🔍 [RecordMode] No move provided to zoom function');
      return;
    }
    
    // Priority: endSquare > to > toSquare for most accurate targeting
    const endSquare = move.endSquare || move.to || move.toSquare;
    
    console.log('🔍 [RecordMode] Found endSquare:', endSquare, 'from move:', {
      endSquare: move.endSquare,
      to: move.to, 
      toSquare: move.toSquare
    });
    
    if (!endSquare) {
      console.warn('🔍 [RecordMode] No endSquare found for zoom');
      return;
    }
    
    // Clear any existing zoom timeout
    if (zoomTimeoutRef.current) {
      clearTimeout(zoomTimeoutRef.current);
      zoomTimeoutRef.current = null;
    }
    
    console.log('🔍 [RecordMode] Activating zoom - isZoomed: true, targetSquare:', endSquare);
    
    // Instant zoom activation
    setBoardZoomConfig({
      isZoomed: true,
      targetSquare: endSquare,
      zoomFactor: 2  // Double the board size
    });
    
    // Reset zoom after duration
    zoomTimeoutRef.current = setTimeout(() => {
      console.log('🔍 [RecordMode] Deactivating zoom after duration');
      setBoardZoomConfig({
        isZoomed: false,
        targetSquare: null,
        zoomFactor: 2
      });
      zoomTimeoutRef.current = null;
    }, duration);
  }, []);

  // Handle local annotation changes
  const handleLocalAnnotationChange = useCallback((moveId, field, value) => {
    setLocalAnnotationEdits(prev => ({
      ...prev,
      [moveId]: {
        ...prev[moveId],
        [field]: value
      }
    }));
  }, []);

  // Helper function to get effective annotation content (local edits override original)
  const getEffectiveAnnotation = useCallback((move, moveId) => {
    if (!move && moveId !== -1) return '';
    
    const localEdits = localAnnotationEditsRef.current[moveId] || {};
    
    // Check for local content first, then local annotation, then original move data
    if (localEdits.content !== undefined) return localEdits.content;
    if (localEdits.annotation !== undefined) return localEdits.annotation;
    
    // Fall back to original move data
    return move?.content || move?.annotation || '';
  }, []);

  // Auto-play functionality for Record mode with TTS integration
  const toggleAutoPlay = useCallback(async () => {
    console.log('[DEBUG] toggleAutoPlay called - isAutoPlaying:', isAutoPlaying);
    
    if (isAutoPlaying) {
      // Stop auto-play and TTS
      if (autoPlayIntervalRef.current) {
        clearTimeout(autoPlayIntervalRef.current);
        autoPlayIntervalRef.current = null;
      }
      await ttsStop();
      setIsAutoPlaying(false);
    } else {
      // Start auto-play with TTS integration
      setIsAutoPlaying(true);
      
      // Reset intro overlay state when starting autoplay
      if (setShowIntroOverlay) setShowIntroOverlay(true);
      if (setIntroTextSlideOut) setIntroTextSlideOut(false);
      
      // Consolidated function for smooth intro to first move transition
      const handleIntroToFirstMoveTransition = async () => {
        // Check if we have a custom intro title with Robot TTS enabled
        const introTitleData = moveEnhancementsRef.current['-1']?.startPositionIntroTitle;
        let introContent;
        
        if (introTitleData?.robotTTS && introTitleData?.text) {
          // Use custom intro title for TTS
          introContent = introTitleData.text;
        } else {
          // Use default logic - combine caption and start annotation
          const caption = localAnnotationEditsRef.current['__title__']?.value || gameMetadata?.caption || '';
          const startAnnotation = state?.startPosition?.annotation || '';
          introContent = caption ? 
            (startAnnotation ? `${caption}. ${startAnnotation}` : caption) : 
            startAnnotation;
        }
        
        if (introContent) {
          // Play intro content first, then transition to first move
          // Speak if EITHER main voiceover is on OR intro has Robot TTS
          if (voiceoverEnabled || introTitleData?.robotTTS) {
            // Use custom TTS function for intro titles if available, otherwise use default
            const ttsFunction = customIntroTTSFunction || ttsSpeak;
            ttsFunction(introContent).catch(error => {
              console.error('TTS error for intro:', error);
            });
          }
          
          const displayTime = await getMoveDisplayTime(-1, introContent);
          
          // If TTS is not enabled, we need to manually trigger slide-out and hide the intro overlay
          if (!voiceoverEnabled && !introTitleData?.robotTTS) {
            // Start slide-out after 1.2 seconds (let it show briefly even without TTS)
            setTimeout(() => {
              if (setIntroTextSlideOut) setIntroTextSlideOut(true);
            }, 1200);
            // Hide intro overlay after slide animation completes (1.2s + 0.5s animation + 0.05s buffer)
            setTimeout(() => {
              if (setShowIntroOverlay) setShowIntroOverlay(false);
              if (setIntroTextSlideOut) setIntroTextSlideOut(false); // Reset for next time
            }, 1750);
          }
          
          autoPlayIntervalRef.current = setTimeout(async () => {
            await transitionToStartPosition();
          }, displayTime);
        } else {
          // No intro content - go directly to move 1 without buffer
          playNextMove();
        }
      };
      
      // Helper function for smooth transition from intro to start position  
      const transitionToStartPosition = async () => {
        if (contextMovesRef.current.length === 0) {
          setIsAutoPlaying(false);
          return;
        }
        
        // Move to start position (show board at initial position)
        goToMove('-1');
        
        // Add 1.5s buffer at start position before proceeding to move 1
        const startPositionBuffer = 1500;
        autoPlayIntervalRef.current = setTimeout(() => {
          playNextMove(); // This will naturally go from -1 to move 0
        }, startPositionBuffer);
      };

      const playNextMove = async () => {
        // Get current position using refs to avoid closure issues
        const currentIdx = contextCurrentMoveRef.current === '-1' ? -1 : parseInt(contextCurrentMoveRef.current.split('|')[0]);
        const nextIdx = currentIdx + 1;
        
        console.log(`[Debug] playNextMove called: currentIdx=${currentIdx}, nextIdx=${nextIdx}`);
        
        // Check if we've reached the end
        if (nextIdx >= contextMovesRef.current.length) {
          console.log('[Debug] Reached end of moves, stopping autoplay');
          setIsAutoPlaying(false);
          return;
        }
        
        if (currentIdx === -1) {
          // We're at start position, transition smoothly to first move
          // Add slight delay before move transition for smoother visual
          await new Promise(resolve => setTimeout(resolve, 100));
          
          goToMove('0');
          
          // Get first move details and continue normal flow
          const move = contextMovesRef.current[0];
          const annotation = getEffectiveAnnotation(move, '0');
          
          // Speak the move annotation if TTS is enabled
          if (voiceoverEnabled && annotation) {
            ttsSpeak(annotation).catch(error => {
              console.error('TTS error for move:', error);
            });
          }
          
          const displayTime = await getMoveDisplayTime(0, annotation);
          
          // Continue with next move after display time
          autoPlayIntervalRef.current = setTimeout(playNextMove, displayTime);
        } else {
          // Go to next move
          goToMove(String(nextIdx));
          
          // Get annotation for this move and trigger zoom if appropriate
          const move = contextMovesRef.current[nextIdx];
          const annotation = getEffectiveAnnotation(move, String(nextIdx));
          
          // Speak the move annotation if TTS is enabled
          if (voiceoverEnabled && annotation) {
            ttsSpeak(annotation).catch(error => {
              console.error('TTS error for move:', error);
            });
          }
          
          const displayTime = await getMoveDisplayTime(nextIdx, annotation);
          
          console.log(`[Debug] Setting setTimeout for move ${nextIdx} with delay: ${displayTime}ms`);
          
          // Trigger zoom animation for moves with zoom enhancement
          const moveId = String(nextIdx);
          const zoomEnhancement = moveEnhancementsRef.current[moveId]?.zoom;
          if (zoomEnhancement && zoomEnhancement.enabled) {
            // Use display time for zoom duration, with a small extension
            const zoomDuration = Math.min(displayTime * 1.2, displayTime + 500);
            triggerMoveSpecificZoom(move, zoomDuration);
          }
          
          autoPlayIntervalRef.current = setTimeout(playNextMove, displayTime);
        }
      };
      
      // Helper function to get display time with TTS integration and speed multiplier
      const getMoveDisplayTime = async (moveIndex, annotation) => {
        try {
          let ttsTime = 0;
          
          // For start position, check if either voiceover is enabled OR intro title has Robot TTS
          const shouldCalculateTTS = moveIndex === -1 ? 
            (voiceoverEnabled || moveEnhancementsRef.current['-1']?.startPositionIntroTitle?.robotTTS) && annotation :
            voiceoverEnabled && annotation;
            
          if (shouldCalculateTTS) {
            // TTS time is already calculated with the adjusted rate from the hook
            ttsTime = await calculateMoveTime(annotation);
          }
          
          // Apply speed multiplier to content timing and add custom delay
          const contentTime = calculateMoveDisplayTime(moveIndex, contextMovesRef.current, state, localAnnotationEditsRef.current);
          
          // Get move enhancement time addition
          const moveId = moveIndex === -1 ? '-1' : moveIndex.toString();
          const timingEnhancement = moveEnhancementsRef.current[moveId]?.timing || {};
          const timeAddition = (timingEnhancement.timeAddition || 0) * 1000; // Convert seconds to milliseconds
          
          // Debug logging for time calculations
          if (timeAddition > 0) {
            console.log(`[Debug] Move ${moveId} time adjustment:`, {
              baseTime: contentTime,
              timeAddition,
              total: contentTime + timeAddition
            });
          }
          
          const adjustedContentTime = (contentTime + timeAddition) / playbackSpeedMultiplier;
          const finalTime = adjustedContentTime + customMoveDelay;
          
          // Debug logging for final time calculation
          console.log(`[Debug] Move ${moveId} final timing:`, {
            baseTime: contentTime,
            timeAddition,
            adjustedContentTime,
            finalTime,
            ttsTime,
            resultTime: Math.max(ttsTime, finalTime)
          });
          
          // Special handling for intro with Robot TTS - use TTS time plus small buffer
          if (moveIndex === -1 && moveEnhancementsRef.current['-1']?.startPositionIntroTitle?.robotTTS && ttsTime > 0) {
            console.log(`[Debug] Using TTS time for intro:`, ttsTime + 200);
            return ttsTime + 200; // Add just 200ms buffer after TTS completes
          }
          
          return Math.max(ttsTime, finalTime);
        } catch (error) {
          console.error('Error calculating display time:', error);
          const fallbackTime = calculateMoveDisplayTime(moveIndex, contextMovesRef.current, state, localAnnotationEditsRef.current);
          return (fallbackTime / playbackSpeedMultiplier) + customMoveDelay;
        }
      };
      
      // Check if we need to show intro content first
      if (contextCurrentMoveRef.current === '-1') {
        // Check if intro is actually enabled/configured before going through intro logic
        const introTitleData = moveEnhancementsRef.current['-1']?.startPositionIntroTitle;
        const caption = localAnnotationEditsRef.current['__title__']?.value || gameMetadata?.caption || '';
        const startAnnotation = state?.startPosition?.annotation || '';
        const hasAnyIntroContent = (introTitleData?.robotTTS && introTitleData?.text) || caption || startAnnotation;
        
        if (hasAnyIntroContent) {
          await handleIntroToFirstMoveTransition();
        } else {
          // No intro at all - go directly to move 1 without any intro logic
          playNextMove();
        }
      } else {
        // Starting from middle of game, proceed normally
        playNextMove();
      }
    }
  }, [isAutoPlaying, goToMove, calculateMoveDisplayTime, state, voiceoverEnabled, ttsSpeak, ttsStop, calculateMoveTime, gameMetadata?.caption, moveEnhancements, triggerMoveSpecificZoom, playbackSpeedMultiplier]);

  // Calculate total recording time when moves or edits change
  useEffect(() => {
    if (contextMoves && state) {
      const time = calculateTotalRecordingTime(contextMoves, state);
      setTotalRecordingTime(time);
    }
  }, [contextMoves, state, calculateTotalRecordingTime, localAnnotationEdits]);

  // Calculate current move time when move or annotations change
  useEffect(() => {
    if (contextMoves && state && contextCurrentMove !== null) {
      const time = calculateCurrentMoveTime(contextCurrentMove, contextMoves, state, localAnnotationEdits, moveEnhancements);
      setCurrentMoveTime(time);
    }
  }, [contextCurrentMove, contextMoves, state, calculateCurrentMoveTime, localAnnotationEdits, moveEnhancements]);

  // Auto-resize Record mode textarea when content changes
  useEffect(() => {
    if (recordTextareaRef.current) {
      const textarea = recordTextareaRef.current;
      // Reset height to auto first, then set to scrollHeight
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }, [contextCurrentMove, localAnnotationEdits]);

  // Clean up auto-play and zoom timeouts on unmount
  useEffect(() => {
    return () => {
      if (autoPlayIntervalRef.current) {
        clearTimeout(autoPlayIntervalRef.current);
        autoPlayIntervalRef.current = null;
      }
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current);
        zoomTimeoutRef.current = null;
      }
    };
  }, []);

  // Focus title input when editing starts
  useEffect(() => {
    if (isEditingTitle && recordTitleInputRef.current) {
      recordTitleInputRef.current.focus();
      recordTitleInputRef.current.select();
    }
  }, [isEditingTitle]);


  const clearMoveEnhancement = useCallback((moveId) => {
    if (!moveId) return;
    
    setMoveEnhancements(prev => {
      const updated = { ...prev };
      delete updated[moveId];
      return updated;
    });
  }, []);

  const hasMoveEnhancements = useCallback((moveId) => {
    if (!moveId || moveId === '-1') return false;
    const enhancements = moveEnhancements[moveId];
    return enhancements && Object.keys(enhancements).length > 0;
  }, [moveEnhancements]);
  
  // Return all logic state and functions instead of JSX
  return {
    // State
    recordSubTab,
    setRecordSubTab,
    isAutoPlaying,
    setIsAutoPlaying,
    totalRecordingTime,
    setTotalRecordingTime,
    currentMoveTime,
    setCurrentMoveTime,
    localAnnotationEdits,
    setLocalAnnotationEdits,
    
    // Move enhancements
    moveEnhancements,
    setMoveEnhancements,
    
    // Title editing
    isEditingTitle,
    setIsEditingTitle,
    recordTitleInputRef,
    recordTextareaRef,
    
    // TTS
    voiceoverEnabled,
    setVoiceoverEnabled,
    ttsRate,
    setTtsRate,
    selectedVoiceIndex,
    setSelectedVoiceIndex,
    availableVoices,
    setAvailableVoices,
    isSpeaking,
    
    // Playback speed
    playbackSpeedMultiplier,
    setPlaybackSpeedMultiplier,
    
    // Background
    backgroundImage,
    setBackgroundImage,
    
    // Board zoom
    boardZoomConfig,
    setBoardZoomConfig,
    
    // Functions
    calculateMoveDisplayTime,
    calculateTotalRecordingTime,
    calculateCurrentMoveTime,
    handleBackgroundImageUpload,
    clearBackgroundImage,
    handleColorSwatchSelect,
    getSquarePosition,
    analyzeMoveShouldZoom,
    triggerBoardZoom,
    triggerMoveSpecificZoom,
    handleLocalAnnotationChange,
    getEffectiveAnnotation,
    toggleAutoPlay,
    
    // Move enhancement functions
    updateMoveEnhancement,
    getMoveEnhancement,
    clearMoveEnhancement,
    hasMoveEnhancements,
    
    // Time adjustment functions
    adjustMoveTime,
    canDecreaseMoveTime,
    getBaseMoveTime,
    
    // TTS functions
    ttsSpeak,
    ttsStop,
    calculateMoveTime,
    
    // Refs
    contextCurrentMoveRef,
    contextMovesRef,
    localAnnotationEditsRef,
    moveEnhancementsRef,
    autoPlayIntervalRef,
    zoomTimeoutRef
  };
};

// Main React Component - Properly structured to re-render when state changes
const RecordModeComponent = (props) => {
  const {
    // State
    recordSubTab,
    setRecordSubTab,
    isAutoPlaying,
    setIsAutoPlaying,
    totalRecordingTime,
    setTotalRecordingTime,
    currentMoveTime,
    setCurrentMoveTime,
    localAnnotationEdits,
    setLocalAnnotationEdits,
    
    // Move enhancements
    moveEnhancements,
    setMoveEnhancements,
    
    // Title editing
    isEditingTitle,
    setIsEditingTitle,
    recordTitleInputRef,
    recordTextareaRef,
    
    // TTS
    voiceoverEnabled,
    setVoiceoverEnabled,
    ttsRate,
    setTtsRate,
    selectedVoiceIndex,
    setSelectedVoiceIndex,
    availableVoices,
    setAvailableVoices,
    isSpeaking,
    
    // Playback speed
    playbackSpeedMultiplier,
    setPlaybackSpeedMultiplier,
    
    // Background
    backgroundImage,
    setBackgroundImage,
    
    // Board zoom - This is the key state that wasn't re-rendering!
    boardZoomConfig,
    setBoardZoomConfig,
    
    // Functions
    calculateMoveDisplayTime,
    calculateTotalRecordingTime,
    calculateCurrentMoveTime,
    handleBackgroundImageUpload,
    clearBackgroundImage,
    handleColorSwatchSelect,
    getSquarePosition,
    analyzeMoveShouldZoom,
    triggerBoardZoom,
    triggerMoveSpecificZoom,
    handleLocalAnnotationChange,
    getEffectiveAnnotation,
    toggleAutoPlay,
    
    // Move enhancement functions
    updateMoveEnhancement,
    getMoveEnhancement,
    clearMoveEnhancement,
    hasMoveEnhancements,
    
    // Time adjustment functions
    adjustMoveTime,
    canDecreaseMoveTime,
    getBaseMoveTime,
    
    // TTS functions
    ttsSpeak,
    ttsStop,
    calculateMoveTime,
    
    // Refs
    contextCurrentMoveRef,
    contextMovesRef,
    localAnnotationEditsRef,
    moveEnhancementsRef,
    autoPlayIntervalRef,
    zoomTimeoutRef
  } = useRecordModeLogic(props);

  console.log('🔍 [RecordModeComponent] Component re-rendering with boardZoomConfig:', boardZoomConfig);
  
  // Destructure props for easier access
  const {
    gameMetadata,
    useChessThreadFont,
    setUseChessThreadFont,
    contextMoves,
    contextCurrentMove,
    contextOrientation,
    boardSize,
    contextStarredMoves,
    state,
    goToMove,
    setViewMode,
    stockfishAnalysis,
    stockfishIsAnalyzing,
    gameHasPuzzleTag,
    puzzleIncorrectMove,
    detectNewMove,
    mobileActiveColor,
    mobileArrowMode,
    mobileArrowColor,
    mobileArrowStart,
    boardRef
  } = props;
  
  return (
    <div style={{
      width: '100vw',
      height: 'calc(100dvh - 55px)',
      backgroundColor: '#000',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '10px',
      boxSizing: 'border-box'
    }}>
      {/* Simple 16:9 Crop Container */}
      <div style={{
        width: 'min(calc((100vw - 20px) * 0.98), calc((100dvh - 130px) * 9/16 * 0.95), 380px)', // 16:9 aspect ratio scaled down 5%
        height: 'min(calc((100dvh - 130px) * 0.98), calc((100vw - 20px) * 16/9 * 0.95))', // 16:9 aspect ratio scaled down 5%
        maxWidth: '380px', // Scaled down max-width by 5%
        border: '2px solid #666',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: '15px',
        boxSizing: 'border-box',
        backgroundColor: (backgroundImage && recordSubTab === 'main') ? 'transparent' : 'transparent',
        backgroundImage: (backgroundImage && recordSubTab === 'main') ? `url(${backgroundImage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}>
      
      {/* Background Overlay for Better Readability */}
      {backgroundImage && recordSubTab === 'main' && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: 0,
          borderRadius: '6px' // Match the container's border radius
        }} />
      )}

      {/* Title and Username Section - Fixed Height - Only show in main tab */}
      {recordSubTab === 'main' && (
        <div style={{
          textAlign: 'center',
          height: '80px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          paddingTop: '35px', // Spacing for Youtube Instagram TikTok Overlay
          position: 'relative',
          zIndex: 1
        }}>
        {/* Title - Editable in Record Mode */}
        {isEditingTitle ? (
          <input
            ref={recordTitleInputRef}
            value={localAnnotationEdits['__title__']?.value || gameMetadata?.caption || 'Chess Game'}
            onChange={(e) => handleLocalAnnotationChange('__title__', 'value', e.target.value)}
            onBlur={() => setIsEditingTitle(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === 'Escape') {
                setIsEditingTitle(false);
              }
            }}
            style={{
              color: '#fff',
              fontSize: '24px',
              fontWeight: 'bold',
              margin: '0 auto 8px auto',
              lineHeight: '1.2',
              letterSpacing: useChessThreadFont ? '2px' : '0px',
              fontFamily: useChessThreadFont ? 'Mansalva, cursive' : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              background: 'rgba(0, 0, 0, 0.5)',
              border: '1px solid gold',
              borderRadius: '4px',
              padding: '8px 12px',
              textAlign: 'center',
              width: 'calc(100% - 20px)',
              maxWidth: '300px',
              display: 'block',
              outline: 'none',
              boxSizing: 'border-box'
            }}
            autoFocus
          />
        ) : (
          <h2 
            onClick={() => setIsEditingTitle(true)}
            style={{
              color: '#fff',
              fontSize: '24px',
              fontWeight: 'bold',
              margin: '0 0 8px 0',
              lineHeight: '1.2',
              letterSpacing: useChessThreadFont ? '2px' : '0px',
              fontFamily: useChessThreadFont ? 'Mansalva, cursive' : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              cursor: 'pointer',
              opacity: 0.9,
              transition: 'opacity 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.opacity = '0.7';
            }}
            onMouseLeave={(e) => {
              e.target.style.opacity = '0.9';
            }}
          >
            {localAnnotationEdits['__title__']?.value || gameMetadata?.caption || 'Chess Game'}
          </h2>
        )}
        <p style={{
          color: 'gold',
          fontSize: '18px',
          margin: 0,
          fontFamily: useChessThreadFont ? 'Mansalva, cursive' : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          letterSpacing: useChessThreadFont ? '1.5px' : '0px',
        }}>
          @{gameMetadata?.creatorHandle || 'Anonymous'}
        </p>
      </div>
      )}

      {/* Conditional Content: Main Record Mode OR Settings */}
      {console.log('🔍 Render path check - recordSubTab:', recordSubTab, 'isZoomed:', boardZoomConfig.isZoomed)}
      {recordSubTab === 'settings' ? (
        /* Settings Subtab */
        <div style={{
          width: '100%',
          height: 'calc(100% - 80px)',
          display: 'flex',
          flexDirection: 'column',
          padding: '20px 0',
          color: '#fff',
          overflowY: 'auto'
        }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: 'bold',
            margin: '0 0 20px 0',
            textAlign: 'center',
            color: '#ff6600',
            fontFamily: 'Mansalva, cursive'
          }}>
            Record Settings
          </h3>
          
          {/* Voice Controls */}
          <div style={{
            backgroundColor: 'rgba(255, 102, 0, 0.1)',
            border: '1px solid #ff6600',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '15px'
          }}>
            {/* Voice Selection */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                fontSize: '14px',
                color: '#ccc'
              }}>
                Voice:
              </label>
              <select
                value={selectedVoiceIndex || ''}
                onChange={(e) => {
                  const voiceIndex = e.target.value === '' ? null : parseInt(e.target.value);
                  setSelectedVoiceIndex(voiceIndex);
                  if (voiceIndex !== null) {
                    localStorage.setItem('chess-record-voice', voiceIndex.toString());
                  } else {
                    localStorage.removeItem('chess-record-voice');
                  }
                }}
                style={{
                  padding: '8px',
                  fontSize: '14px',
                  borderRadius: '6px',
                  border: '1px solid #666',
                  backgroundColor: '#333',
                  color: '#fff',
                  width: '100%'
                }}
              >
                <option value="">System Default</option>
                {availableVoices.map((voice, index) => (
                  <option key={index} value={index}>
                    {voice.name} ({voice.lang})
                  </option>
                ))}
              </select>
            </div>
            
            {/* Speech Rate */}
            <div style={{ marginBottom: '0' }}>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                fontSize: '14px',
                color: '#ccc'
              }}>
                Speech Rate: {ttsRate.toFixed(1)}x
              </label>
              <input
                type="range"
                min="0.5"
                max="2.0"
                step="0.1"
                value={ttsRate}
                onChange={(e) => {
                  const rate = parseFloat(e.target.value);
                  setTtsRate(rate);
                  localStorage.setItem('chess-voice-rate', rate.toString());
                }}
                style={{
                  width: '100%',
                  accentColor: '#ff6600'
                }}
              />
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '12px',
                color: '#999',
                marginTop: '4px'
              }}>
                <span>Slow (0.5x)</span>
                <span>Fast (2.0x)</span>
              </div>
            </div>
          </div>
          
          {/* Display Settings */}
          <div style={{
            backgroundColor: 'rgba(255, 102, 0, 0.1)',
            border: '1px solid #ff6600',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '15px'
          }}>
            {/* Font Selection */}
            <div style={{ marginBottom: '0' }}>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                fontSize: '14px',
                color: '#ccc'
              }}>
                Title Font:
              </label>
              <select
                value={useChessThreadFont ? 'chessthread' : 'basic'}
                onChange={(e) => {
                  const useChessThread = e.target.value === 'chessthread';
                  setUseChessThreadFont(useChessThread);
                  localStorage.setItem('chess-use-chessthread-font', JSON.stringify(useChessThread));
                }}
                style={{
                  padding: '8px',
                  fontSize: '14px',
                  borderRadius: '6px',
                  border: '1px solid #666',
                  backgroundColor: '#333',
                  color: '#fff',
                  width: '100%'
                }}
              >
                <option value="chessthread">ChessThread</option>
                <option value="basic">Basic Font</option>
              </select>
            </div>
          </div>
          
          {/* Background Settings */}
          <div style={{
            backgroundColor: 'rgba(255, 102, 0, 0.1)',
            border: '1px solid #ff6600',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '5px'
          }}>
            <div style={{ marginBottom: '15px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <label style={{
                  fontSize: '14px',
                  color: '#ccc',
                  margin: 0
                }}>
                  Background:
                </label>
                
                {/* Upload Button - Flush Right */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleBackgroundImageUpload}
                    style={{
                      display: 'none'
                    }}
                    id="background-image-upload"
                  />
                  <label
                    htmlFor="background-image-upload"
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      borderRadius: '6px',
                      border: '1px solid #666',
                      backgroundColor: '#333',
                      color: '#fff',
                      cursor: 'pointer',
                      textAlign: 'center'
                    }}
                  >
                    {backgroundImage ? 'Change' : 'Upload Image'}
                  </label>
                  
                  {backgroundImage && (
                    <button
                      onClick={clearBackgroundImage}
                      style={{
                        padding: '6px 12px',
                        fontSize: '12px',
                        borderRadius: '6px',
                        border: '1px solid #ff0050',
                        backgroundColor: 'transparent',
                        color: '#ff0050',
                        cursor: 'pointer'
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
              
              {/* Background Preview */}
              {backgroundImage && (
                <div style={{
                  width: '100%',
                  height: '60px',
                  backgroundImage: `url(${backgroundImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  borderRadius: '6px',
                  border: '1px solid #666',
                  marginBottom: '10px',
                  position: 'relative'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '5px',
                    right: '5px',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    borderRadius: '4px',
                    padding: '2px 6px',
                    fontSize: '10px',
                    color: '#ccc'
                  }}>
                    16:9 Preview
                  </div>
                </div>
              )}
              
              {/* Color Swatches */}
              <div style={{
                display: 'flex',
                gap: '8px',
                flexWrap: 'wrap',
                marginBottom: '10px'
              }}>
                {[
                  '#1a1a1a', // Dark Gray
                  '#2c2c54', // Dark Blue
                  '#40407a', // Purple
                  '#ff6b6b', // Red
                  '#feca57', // Yellow
                  '#48cae4', // Light Blue
                  '#06ffa5', // Green
                  '#ff9ff3', // Pink
                  '#54a0ff', // Blue
                  '#5f27cd', // Deep Purple
                  '#00d2d3', // Cyan
                  '#ff9f43'  // Orange
                ].map((color, index) => (
                  <button
                    key={color}
                    onClick={() => handleColorSwatchSelect(color)}
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '4px',
                      backgroundColor: color,
                      border: '2px solid #666',
                      cursor: 'pointer',
                      transition: 'transform 0.1s ease',
                      flexShrink: 0
                    }}
                    onMouseOver={(e) => {
                      e.target.style.transform = 'scale(1.1)';
                      e.target.style.border = '2px solid #fff';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.transform = 'scale(1)';
                      e.target.style.border = '2px solid #666';
                    }}
                    title={`Set background to ${color}`}
                  />
                ))}
              </div>
              
              <div style={{
                fontSize: '11px',
                color: '#999',
                marginTop: '10px',
                lineHeight: '1.3'
              }}>
                Upload a 16:9 image. Image will be cropped to fit.
              </div>
            </div>
          </div>
          
          {/* Board Animation Settings */}
          <div style={{
            backgroundColor: 'rgba(255, 102, 0, 0.1)',
            border: '1px solid #ff6600',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '15px'
          }}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                color: '#ccc'
              }}>
                Board Animations:
              </label>
              
              {/* Auto-Zoom Toggle */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '10px'
              }}>
                <input
                  type="checkbox"
                  checked={boardZoomConfig.autoZoom}
                  onChange={(e) => setBoardZoomConfig(prev => ({
                    ...prev,
                    autoZoom: e.target.checked
                  }))}
                  style={{
                    width: '16px',
                    height: '16px',
                    marginRight: '8px',
                    accentColor: '#ff6600'
                  }}
                  id="auto-zoom-checkbox"
                />
                <label
                  htmlFor="auto-zoom-checkbox" 
                  style={{
                    fontSize: '14px',
                    color: '#ccc',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Auto-zoom on exciting moves
                </label>
              </div>
              
              {/* Manual Zoom Controls */}
              <div style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center'
              }}>
                <button
                  onClick={() => {
                    console.log('🔍 Manual zoom button clicked - current boardZoomConfig:', boardZoomConfig);
                    const currentMove = getMove(contextMoves, contextCurrentMove);
                    console.log('🔍 Current move:', currentMove);
                    
                    if (currentMove && (currentMove.toSquare || currentMove.to || currentMove.endSquare)) {
                      const endSquare = currentMove.toSquare || currentMove.to || currentMove.endSquare;
                      console.log('🔍 Found endSquare:', endSquare, 'toggling zoom to:', !boardZoomConfig.isZoomed);
                      
                      setBoardZoomConfig(prev => {
                        const newConfig = {
                          isZoomed: !prev.isZoomed,
                          targetSquare: !prev.isZoomed ? endSquare : null,
                          zoomFactor: 2
                        };
                        console.log('🔍 Setting new boardZoomConfig:', newConfig);
                        return newConfig;
                      });
                    } else {
                      console.log('🔍 No valid current move found for manual zoom');
                    }
                  }}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    borderRadius: '6px',
                    border: '1px solid #666',
                    backgroundColor: boardZoomConfig.isZoomed ? '#ff6600' : '#333',
                    color: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  {boardZoomConfig.scale > 1 ? 'Reset Zoom' : 'Zoom Current'}
                </button>
                
                <span style={{
                  fontSize: '11px',
                  color: '#999'
                }}>
                  Scale: {boardZoomConfig.scale.toFixed(1)}x
                </span>
              </div>
              
              <div style={{
                fontSize: '11px',
                color: '#999',
                marginTop: '6px',
                lineHeight: '1.3'
              }}>
                Automatically zoom in on captures, checks, and tactical moves during auto-play.
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Main Record Mode Content */
        <>
      {/* Chess Board - Viewport Container with Direct Size Zoom */}
      {console.log('🔍 Board container JSX render, isZoomed:', boardZoomConfig.isZoomed)}
      <div style={{
        width: `${window.calculateRecordModeBoardSize?.() || boardSize || 350}px`,
        height: `${window.calculateRecordModeBoardSize?.() || boardSize || 350}px`,
        marginBottom: '8px',
        flexShrink: 0,
        position: 'relative',
        zIndex: 1,
        overflow: 'hidden', // Clip the enlarged board
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        
        {/* Board with Dynamic Size and Translation - Debug Version */}
        <div style={{
          width: (() => {
            const baseSize = window.calculateRecordModeBoardSize?.() || boardSize || 350;
            const finalSize = boardZoomConfig.isZoomed ? baseSize * boardZoomConfig.zoomFactor : baseSize;
            console.log('🔍 BOARD CONTAINER SIZE CALCULATION:', { 
              baseSize, 
              finalSize, 
              isZoomed: boardZoomConfig.isZoomed, 
              zoomFactor: boardZoomConfig.zoomFactor,
              windowFunctionExists: !!window.calculateRecordModeBoardSize,
              boardSizeProp: boardSize,
              finalWidthString: `${finalSize}px`
            });
            return `${finalSize}px`;
          })(),
          height: (() => {
            const baseSize = window.calculateRecordModeBoardSize?.() || boardSize || 350;
            const finalSize = boardZoomConfig.isZoomed ? baseSize * boardZoomConfig.zoomFactor : baseSize;
            console.log('🔍 BOARD CONTAINER HEIGHT:', `${finalSize}px`);
            return `${finalSize}px`;
          })(),
          transform: (() => {
            if (!boardZoomConfig.isZoomed || !boardZoomConfig.targetSquare) {
              console.log('🔍 No zoom or no target square - translate(0, 0)');
              return 'translate(0, 0)';
            }
            
            const baseSize = window.calculateRecordModeBoardSize?.() || boardSize || 350;
            const zoomedSize = baseSize * boardZoomConfig.zoomFactor;
            
            // Get target square position on the zoomed board
            const squarePos = getSquarePosition(
              boardZoomConfig.targetSquare,
              zoomedSize,
              contextOrientation || 'white'
            );
            
            // Calculate translation to center target square in viewport
            const viewportCenter = baseSize / 2;
            let translateX = viewportCenter - squarePos.x;
            let translateY = viewportCenter - squarePos.y;
            
            // Constrain translation to prevent showing empty space
            const maxTranslate = (zoomedSize - baseSize) / 2;
            translateX = Math.max(-maxTranslate, Math.min(maxTranslate, translateX));
            translateY = Math.max(-maxTranslate, Math.min(maxTranslate, translateY));
            
            const result = `translate(${translateX}px, ${translateY}px)`;
            console.log('🔍 Transform result:', result, 'targetSquare:', boardZoomConfig.targetSquare);
            return result;
          })(),
          transition: 'none', // Instant change as requested
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: boardZoomConfig.isZoomed ? 'rgba(255, 0, 0, 0.1)' : 'transparent', // Debug red tint when zoomed
          border: boardZoomConfig.isZoomed ? '2px solid red' : 'none' // Debug border
        }}>
        <BoardThree
          boardSize={(() => {
            const baseSize = window.calculateRecordModeBoardSize?.() || boardSize || 350;
            const finalBoardSize = boardZoomConfig.isZoomed ? baseSize * boardZoomConfig.zoomFactor : baseSize;
            console.log('🔍 BOARDTHREE BOARDSIZE PROP:', {
              baseSize,
              finalBoardSize,
              isZoomed: boardZoomConfig.isZoomed,
              zoomFactor: boardZoomConfig.zoomFactor,
              'ACTUAL PROP VALUE': finalBoardSize
            });
            return finalBoardSize;
          })()}
          ref={boardRef}
          
          boardStyle={{
            borderRadius: '8px',
            boxShadow: '0 0 20px rgba(0,0,0,0.5)'
          }}
          stockfishAnalysis={stockfishAnalysis}
          stockfishIsAnalyzing={stockfishIsAnalyzing}
          isMobile={true}
          isPuzzleMode={gameHasPuzzleTag}
          puzzleIncorrectMove={puzzleIncorrectMove}
          showPuzzleOverlay={gameHasPuzzleTag}
          onNewMove={detectNewMove}
          mobileActiveColor={mobileActiveColor}
          mobileArrowMode={mobileArrowMode}
          mobileArrowColor={mobileArrowColor}
          mobileArrowStart={mobileArrowStart}
          onMobileArrowEnd={(endSquare) => {
            if (boardRef.current) {
              boardRef.current.handleMobileArrowEnd(endSquare);
            }
          }}
        />
        </div> {/* Close dynamic board wrapper */}
      </div> {/* Close viewport container */}

      {/* Conditional Content Area - Takes Remaining Space */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        overflow: 'hidden'
      }}>
{(() => {
        const currentMove = getMove(contextMoves, contextCurrentMove);
        const originalMoveAnnotation = currentMove?.annotation || '';
        const originalMoveContent = currentMove?.content || '';
        const moveNotation = currentMove?.notation || '';
        
        // Create unique move ID for local editing
        const currentMoveId = contextCurrentMove === '-1' ? '-1' : (contextCurrentMove || 'start');
        const localEdits = localAnnotationEdits[currentMoveId] || {};
        
        // Use local edits if available, fallback to original
        const moveAnnotation = localEdits.annotation !== undefined ? localEdits.annotation : originalMoveAnnotation;
        const moveContent = localEdits.content !== undefined ? localEdits.content : originalMoveContent;
        
        // Calculate move number for display
        let moveDisplay = '';
        if (contextCurrentMove !== '-1' && moveNotation) {
          const moveIndex = parseInt(contextCurrentMove.split('|')[0]);
          const moveNumber = Math.floor(moveIndex / 2) + 1;
          const isWhite = moveIndex % 2 === 0;
          moveDisplay = `${moveNumber}${isWhite ? '.' : '...'} ${moveNotation}`;
        }
        
        const hasAnnotation = !!(moveAnnotation || moveContent);
        
        return (
          <div style={{
            position: 'relative',
            width: '100%',
            maxWidth: '400px',
            overflow: 'hidden',
            zIndex: 1
          }}>
            {/* Layer 1: MobileMovesEngine with Slider (normal flow - maintains container height) */}
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              boxSizing: 'border-box',
              padding: '0px 10px',
              opacity: hasAnnotation ? 0 : 1,
              pointerEvents: hasAnnotation ? 'none' : 'auto'
            }}>
              {/* Move Slider */}
              <div style={{
                width: '100%',
                height: '28px',
                marginBottom: '4px',
                flexShrink: 0,
                padding: '0 20px',
                boxSizing: 'border-box'
              }}>
                <style>
                  {`
                    .progress-slider::-webkit-slider-thumb {
                      -webkit-appearance: none !important;
                      appearance: none !important;
                      height: 0 !important;
                      width: 0 !important;
                      background: transparent !important;
                      cursor: pointer;
                    }
                    .progress-slider::-moz-range-thumb {
                      height: 0 !important;
                      width: 0 !important;
                      background: transparent !important;
                      cursor: pointer;
                      border: none !important;
                    }
                  `}
                </style>
                <input
                  type="range"
                  min="-1"
                  max={contextMoves.length - 1}
                  value={contextCurrentMove === '-1' ? -1 : parseInt(contextCurrentMove.split('|')[0])}
                  className="progress-slider"
                  onChange={(e) => {
                    if (isAutoPlaying) {
                      if (autoPlayIntervalRef.current) {
                        clearTimeout(autoPlayIntervalRef.current);
                      }
                      setIsAutoPlaying(false);
                    }
                    goToMove(e.target.value === '-1' ? '-1' : e.target.value);
                  }}
                  style={{
                    width: '100%',
                    height: '10px',
                    cursor: 'pointer',
                    accentColor: '#58b047',
                    WebkitAppearance: 'none',
                    appearance: 'none',
                    background: `linear-gradient(to right, #58b047 0%, #58b047 ${((parseInt(contextCurrentMove === '-1' ? -1 : contextCurrentMove.split('|')[0]) + 1) / (contextMoves.length)) * 100}%, #333 ${((parseInt(contextCurrentMove === '-1' ? -1 : contextCurrentMove.split('|')[0]) + 1) / (contextMoves.length)) * 100}%, #333 100%)`,
                    borderRadius: '10px',
                    outline: 'none'
                  }}
                />
              </div>
              
              {/* MobileMovesEngine */}
              <div style={{
                overflow: 'hidden',
                minHeight: '0',
                padding: '0px 5px'
              }}>
                <MobileMovesEngine
                  moves={contextMoves}
                  currentMove={contextCurrentMove}
                  goToMove={goToMove}
                  orientation={contextOrientation}
                  isMobile={true}
                  boardSize={Math.min(boardSize, window.innerWidth - 40)}
                  starredMoves={contextStarredMoves}
                  startPosition={state.startPosition}
                  isCommentMode={false}
                  onEnterCommentMode={() => {}}
                  commentMoves={[]}
                  currentCommentMoveIndex={-1}
                  hideTabSidebar={true}
                  hideMainLineHeader={true}
                />
              </div>
            </div>
            
            {/* Layer 2: Annotation Overlay (absolute positioning on top) */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              boxSizing: 'border-box',
              opacity: hasAnnotation ? 1 : 0,
              pointerEvents: hasAnnotation ? 'auto' : 'none',
              zIndex: 2,
              backgroundColor: hasAnnotation ? 'transparent' : 'transparent'
            }}>
              <div style={{
                width: '100%',
                maxWidth: '320px',
                margin: '10px auto',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'center',
                boxSizing: 'border-box',
                backgroundColor: '#58b04740',
                  border: '1px solid #58b047',
                  textAlign: 'center',
                  borderRadius: '6px',
                  padding: '10px'

              }}>
            
                  {/* Green Move Button Indicator */}
                  {moveDisplay && (
                    <div style={{
                      display: 'inline-block',
                      backgroundColor: '#58b047',
                      color: 'black',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      marginBottom: '8px',
                      maxWidth: '60px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {moveDisplay}
                    </div>
                  )}
{(moveContent || true) && (
                    <textarea
                      ref={recordTextareaRef}
                      value={moveContent}
                      onChange={(e) => handleLocalAnnotationChange(currentMoveId, 'content', e.target.value)}
                      placeholder={!moveContent ? 'Add content annotation...' : ''}
                      style={{
                        color: 'white',
                        fontSize: '16px',
                        fontStyle: 'italic',
                        fontWeight: 'bold',
                        background: 'transparent',
                        border: 'none',
                        width: '100%',
                        textAlign: 'center',
                        // fontFamily: 'inherit',
                        resize: 'none',
                        minHeight: '20px',
                      }}
                      // rows={1}
                      onInput={(e) => {
                        // Auto-resize textarea to fit content
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                    />
                  )}
              </div>
            </div>
          </div>
        );
      })()}
      
      </div> {/* Close Conditional Content Area */}
        </>
      )}
      
      {/* Fixed Bottom ChessThread Logo - Can be overwritten by annotation overlay */}
      <div style={{
        position: 'absolute',
        bottom: '15px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: 'gold',
        fontSize: '16px',
        fontFamily: 'Mansalva, cursive',
        letterSpacing: '1px',
        opacity: 0.8,
        pointerEvents: 'none',
        zIndex: 1
      }}>
        <img 
          src={chessslogo} 
          alt="ChessThread Logo" 
          style={{ 
            width: '40px', 
            height: '40px',
            objectFit: 'contain'
          }} 
        />
        <span>ChessThread</span>
      </div>
      
      </div> {/* Close 16:9 container */}
      
      {/* External Auto-play Controls Panel */}
      <div style={{
        width: 'min(calc((100vw - 20px) * 0.95), calc((100dvh - 130px) * 9/16 * 0.95), 380px)', // Same width as main container (scaled down 5%)
        maxWidth: '380px', // Same max-width constraint (scaled down 5%)
        height: '60px', // Much shorter height for controls only
        border: '2px solid #666',
        display: 'flex',
        gap: '15px',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 10px',
        marginTop: '10px',
        backgroundColor: 'transparent',
        boxSizing: 'border-box'
      }}>
        {/* Back Button - Left */}
        <button
          onClick={() => setViewMode('View')}
          style={{
            backgroundColor: '#666',
            border: 'none',
            color: '#fff',
            padding: '8px 12px',
            borderRadius: '20px',
            fontSize: '18px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <i className="fa-solid fa-arrow-left"></i>
        </button>
        
        {/* Center section with Voiceover Toggle, Settings, and Total Time */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Voiceover Toggle Button */}
           {/* Current Move and Total Time Display */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* Current Move Time */}
            <div style={{ color: '#ff6600', fontSize: '14px', fontWeight: 'bold' }}>
              {currentMoveTime}
            </div>
            <div style={{ color: '#666', fontSize: '12px' }}>|</div>
            {/* Total Time */}
            <div style={{ color: '#ff0050', fontSize: '14px', fontWeight: 'bold' }}>
              {totalRecordingTime}
            </div>
          </div>
          <button
            onClick={() => setVoiceoverEnabled(!voiceoverEnabled)}
            style={{
              backgroundColor: voiceoverEnabled ? '#9b59b6' : '#666',
              border: 'none',
              color: '#fff',
              padding: '6px 10px',
              borderRadius: '15px',
              fontSize: '16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              transition: 'background-color 0.2s'
            }}
            title={voiceoverEnabled ? 'Voiceover ON' : 'Voiceover OFF'}
          >
            <i className={voiceoverEnabled ? 'fa-solid fa-volume-high' : 'fa-solid fa-volume-xmark'}></i>
            {isSpeaking && <i className="fa-solid fa-waveform-lines" style={{ fontSize: '12px' }}></i>}
          </button>
          
          {/* Settings Button */}
          <button
            onClick={() => setRecordSubTab(recordSubTab === 'settings' ? 'main' : 'settings')}
            style={{
              backgroundColor: recordSubTab === 'settings' ? '#ff6600' : '#666',
              border: 'none',
              color: '#fff',
              padding: '6px 10px',
              borderRadius: '15px',
              fontSize: '16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              transition: 'background-color 0.2s'
            }}
            title="Record Settings"
          >
            <i className="fa-solid fa-gear"></i>
          </button>
             {/* Play/Pause Button - Right */}
        <button
          onClick={toggleAutoPlay}
          style={{
            backgroundColor: isAutoPlaying ? '#ff0050' : '#58b047',
            border: 'none',
            color: '#fff',
            padding: '8px 12px',
            borderRadius: '20px',
            fontSize: '18px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <i className={isAutoPlaying ? 'fa-solid fa-pause' : 'fa-solid fa-play'}></i>
        </button>
    
        </div>
        
     
      </div>
    </div>
  );
};

export default RecordModeComponent;